import React from 'react';
import { NextPage, NextPageContext } from 'next';
import Head from 'next/head';
import Cookie from 'js-cookie';
import NextSeo from 'next-seo';
import { Image } from 'shared-types/Common';
import { Property } from 'shared-types/Property';
import { useTranslations } from '@lang';
import { fetchBasketDetails } from '@store/basket/actions';
import { getSettings } from '@store/setting/actions';
import BasketContainer from '@routes/Basket/containers/Basket';
import { NormalizedStore } from '@store/entity/entity.types';

type BasketProps = {
  language: string;
  propertyId: number;
  propertyTitle: string;
  socialFallbackImage: Image;
  url: string;
};

const Basket: NextPage<BasketProps> = ({
  language,
  propertyId,
  propertyTitle,
  socialFallbackImage,
  url,
}) => {
  const { t } = useTranslations();
  const pageName = t('app.basket');

  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      <BasketContainer language={language} propertyId={propertyId} />
    </>
  );
};

type BasketInitialProps = {
  property: Property;
  store: NormalizedStore;
  language: string;
  url: string;
} & NextPageContext;

Basket.getInitialProps = async ({ store, property, language, url }: BasketInitialProps) => {
  const basketId = Cookie.get('basketId') as unknown as number;
  await Promise.all([
    ...(basketId
      ? [
          fetchBasketDetails(
            property.id,
            basketId,
            language,
            property.slug,
            property.timezone
          )(store.dispatch),
        ]
      : []),
    getSettings()(store.dispatch),
  ]);
  return {
    url,
    language,
    propertyId: property.id,
    propertyTitle: property.propertyTitle,
    socialFallbackImage: property.socialFallbackImage,
  };
};

export default Basket;
